import * as React from 'react';
import { FieldPath, FieldValues, PathValue, useController, UseControllerProps } from 'react-hook-form';

import { Autocomplete } from '../Autocomplete/Autocomplete';
import { ChipProps } from '../Chip/Chip';
import { TextField, TextFieldProps } from '../TextField/TextField';

type SplitStringModeProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TValue = PathValue<TFieldValues, TName>
> =
  | {
      // -- SplitStringMode props -- //
      splitStringDelimiter: string;
      // -- Autocomplete Props -- //
      onChange: (event: React.SyntheticEvent, value: TValue, reason: string) => void;
    }
  | {
      // -- SplitStringMode props -- //
      splitStringDelimiter?: never;
      // -- Autocomplete Props -- //
      onChange?: (event: React.SyntheticEvent, value: TValue, reason: string) => void;
    };

export type AutocompleteFormItemProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TValue = PathValue<TFieldValues, TName>
> = {
  // -- UseControllerProps -- //
  name: UseControllerProps<TFieldValues, TName>['name'];
  control: UseControllerProps<TFieldValues>['control'];
  /**
   * @default []
   */
  defaultValue?: UseControllerProps<TFieldValues, TName>['defaultValue'];

  // -- TextFieldProps -- //
  variant?: TextFieldProps['variant'];
  disabled?: TextFieldProps['disabled'];
  label: TextFieldProps['label'];
  placeholder?: TextFieldProps['placeholder'];
  fullWidth?: TextFieldProps['fullWidth'];
  required?: TextFieldProps['required'];

  // -- Chip Props -- //
  inputMode?: ChipProps['inputMode'];
} & SplitStringModeProps<TFieldValues, TName, TValue>;

export const AutocompleteFormItem = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TValue = PathValue<TFieldValues, TName>
>(
  props: AutocompleteFormItemProps<TFieldValues, TName, TValue>
) => {
  const {
    name,
    control,
    defaultValue,
    onChange: onChangeFromProps,
    inputMode,
    disabled,
    variant,
    required,
    fullWidth,
    label,
    placeholder
  } = props;

  const {
    field: { ref, onChange, value },
    fieldState: { error }
  } = useController({
    name,
    control,
    defaultValue: (defaultValue ?? []) as PathValue<TFieldValues, TName>
  });

  let normalizedValue = value;
  if (props.splitStringDelimiter && typeof value === 'string') {
    normalizedValue = value === '' ? [] : value.split(props.splitStringDelimiter);
  }

  return (
    <Autocomplete
      multiple
      fullWidth={fullWidth}
      options={[]}
      value={(normalizedValue as unknown as string[]) || []}
      onChange={(e, newValue, reason) => {
        if (onChangeFromProps) {
          onChangeFromProps(e, newValue as TValue, reason);
        } else {
          onChange(newValue);
        }
      }}
      freeSolo
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={ref}
          label={label}
          placeholder={placeholder}
          error={!!error}
          helperText={error ? error.message : ''}
          required={required}
          fullWidth={fullWidth}
          disabled={disabled}
          variant={variant}
          inputMode={inputMode}
        />
      )}
    />
  );
};
